import { useState, useCallback, useEffect, RefObject } from 'react';
import { useOffenderStore } from '@/stores/offenderStore';

interface UseZoomProps {
  containerRef: RefObject<HTMLDivElement>;
  imageRef: RefObject<HTMLImageElement>;
}

export interface UseZoomReturn {
  zoomLevel: number;
  panPosition: { x: number; y: number };
  isDragging: boolean;
  isDragZoom: boolean;
  selectionBox: { x: number; y: number; width: number; height: number } | null;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleWheel: (e: WheelEvent) => void;
  resetZoom: () => void;
}

export function useZoom({ containerRef, imageRef }: UseZoomProps): UseZoomReturn {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isDragZoom, setIsDragZoom] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectionBox, setSelectionBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const { addingOffender } = useOffenderStore();

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  const zoomToPoint = useCallback(
    (clientX: number, clientY: number, targetZoom: number) => {
      if (addingOffender) return;

      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = clientX - rect.left;
      const y = clientY - rect.top;

      setPanPosition({
        x: rect.width / 2 - x * targetZoom,
        y: rect.height / 2 - y * targetZoom,
      });
      setZoomLevel(targetZoom);
    },
    [imageRef, addingOffender]
  );

  const calculateZoomLevel = useCallback((width: number, height: number, rect: DOMRect) => {
    const widthRatio = width / rect.width;
    const heightRatio = height / rect.height;
    const selectionRatio = Math.max(widthRatio, heightRatio);
    return Math.min(5, Math.max(1.5, 1 / selectionRatio));
  }, []);

  const handleZoom = useCallback(
    (clientX: number, clientY: number) => {
      if (addingOffender) return;

      if (zoomLevel > 1) {
        resetZoom();
      } else {
        zoomToPoint(clientX, clientY, 3);
      }
    },
    [zoomLevel, resetZoom, zoomToPoint, addingOffender]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (addingOffender) return;

      if ((e.target as HTMLElement).closest('[data-no-zoom]')) {
        return;
      }

      if (e.button === 0) {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setSelectionBox(null);
      }
    },
    [addingOffender]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (addingOffender) return;

      if (!isDragging || zoomLevel > 1) return;

      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.min(dragStart.x, e.clientX) - rect.left;
      const y = Math.min(dragStart.y, e.clientY) - rect.top;
      const width = Math.abs(e.clientX - dragStart.x);
      const height = Math.abs(e.clientY - dragStart.y);

      if (width > 5 || height > 5) {
        setIsDragZoom(true);
      }

      if (zoomLevel === 1) {
        const clampedX = Math.max(0, Math.min(x, rect.width));
        const clampedY = Math.max(0, Math.min(y, rect.height));
        const clampedWidth = Math.min(width, rect.width - clampedX);
        const clampedHeight = Math.min(height, rect.height - clampedY);

        setSelectionBox({
          x: clampedX,
          y: clampedY,
          width: clampedWidth,
          height: clampedHeight,
        });
      }
    },
    [isDragging, dragStart, zoomLevel, imageRef, addingOffender]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (addingOffender) return;

      if (!isDragging) return;

      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) {
        setIsDragging(false);
        setSelectionBox(null);
        return;
      }

      setIsDragging(false);

      if (zoomLevel > 1) {
        handleZoom(e.clientX, e.clientY);
        setSelectionBox(null);
        setIsDragZoom(false);
        return;
      }

      const MIN_DRAG_SIZE = 5;
      const dragDistance = Math.sqrt(
        Math.pow(e.clientX - dragStart.x, 2) + Math.pow(e.clientY - dragStart.y, 2)
      );

      if (dragDistance < MIN_DRAG_SIZE) {
        handleZoom(e.clientX, e.clientY);
        setSelectionBox(null);
        setIsDragZoom(false);
        return;
      }

      if (
        selectionBox &&
        (selectionBox.width > MIN_DRAG_SIZE || selectionBox.height > MIN_DRAG_SIZE)
      ) {
        const selectionCenterX = e.clientX - selectionBox.width / 2;
        const selectionCenterY = e.clientY - selectionBox.height / 2;
        const zoomLevel = calculateZoomLevel(selectionBox.width, selectionBox.height, rect);
        zoomToPoint(selectionCenterX, selectionCenterY, zoomLevel);
      }

      setSelectionBox(null);
      setIsDragZoom(false);
    },
    [
      isDragging,
      dragStart,
      selectionBox,
      handleZoom,
      zoomToPoint,
      zoomLevel,
      calculateZoomLevel,
      imageRef,
      addingOffender,
    ]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (addingOffender) return;

      e.preventDefault();
      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect) return;

      const delta = -e.deltaY;
      const zoomFactor = 1.1;

      setZoomLevel((currentZoom) => {
        const newZoom = delta > 0 ? currentZoom * zoomFactor : currentZoom / zoomFactor;
        return Math.min(Math.max(newZoom, 1), 5);
      });
    },
    [imageRef, addingOffender]
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel, containerRef]);

  return {
    zoomLevel,
    panPosition,
    isDragging,
    isDragZoom,
    selectionBox,
    handleMouseDown,
    handleWheel,
    resetZoom,
  };
}
